import { useState } from "react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Check } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { PROVIDER_LABELS, Provider, type StartConnectionResult } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChannelIcon } from "@/components/workspace/channel-icon"
import { Heading, Hint } from "@/components/workspace/primitives"
import { SetupSteps } from "@/components/workspace/setup-steps"
import { api, ApiError } from "@/lib/api"
import { channelsQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/channels/new")({
  validateSearch: z.object({ provider: Provider.optional() }),
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_workspace")) throw redirect({ to: "/w/$workspace/settings/channels", params })
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(channelsQuery(params.workspace)),
  component: ChannelPreflight,
})

const CHECKS: Record<Provider, Array<[string, string]>> = {
  instagram: [
    ["A professional Instagram account", "Use an account supported by the business integration."],
    ["Permission to manage the account", "Make sure you have access to the correct business and assets."],
    ["Messaging and comment access", "Choose the permissions for the features your team needs."],
  ],
  messenger: [
    ["A Facebook Page for your business", "Use an account supported by the business integration."],
    ["Permission to manage the account", "Make sure you have access to the correct business and assets."],
    ["Messaging and comment access", "Choose the permissions for the features your team needs."],
  ],
}

/** Screen 05. Ends by handing the browser to the provider's authorisation dialog. */
function ChannelPreflight() {
  const { workspace } = useWorkspaceContext()
  const { provider: preselected } = Route.useSearch()
  const { sandbox } = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const [channel, setChannel] = useState<Provider>(preselected ?? "instagram")
  const start = useMutation({
    mutationFn: (provider: Provider) => api<StartConnectionResult>(`/w/${workspace.slug}/channels/connect`, { method: "POST", body: JSON.stringify({ provider }) }),
    onSuccess: (result) => window.location.assign(result.authorizationUrl),
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not start the connection"),
  })
  return (
    <div className="page-wrap ws-workspace">
      <Heading title="Connect a channel" description="Bring customer conversations into one shared inbox." />
      <SetupSteps active={1} />
      <div className="ws-connect-grid">
        <div className="ws-channel-choices">
          <h2>Choose your channel</h2>
          {Provider.options.map((name) => (
            <button key={name} type="button" onClick={() => setChannel(name)} className={channel === name ? "selected" : ""} aria-pressed={channel === name}>
              <ChannelIcon provider={name} size={24} strokeWidth={1.5} />
              <strong>{PROVIDER_LABELS[name]}</strong>
              <p>{name === "instagram" ? "Direct messages, story replies and comments." : "Conversations from your Facebook Page."}</p>
              {channel === name && <Check size={16} className="ws-choice-check" />}
            </button>
          ))}
          <p className="ws-hint">Add more accounts at any time in Settings → Channels.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>A quick check before {PROVIDER_LABELS[channel]}</CardTitle>
            <CardDescription>Use the Meta account that manages your business.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="ws-access-checks">
              {CHECKS[channel].map(([title, detail], index) => (
                <div key={title}>
                  <span className="ws-step-marker">{index + 1}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{detail}</p>
                  </div>
                </div>
              ))}
            </div>
            <Hint title="Your team stays in control">Vienna only accesses the accounts you select. You can review each connection’s capabilities before continuing.</Hint>
            <div className="ws-form-footer">
              <Button variant="ghost" asChild>
                <Link to="/w/$workspace/settings/channels" params={{ workspace: workspace.slug }}>
                  <ArrowLeft />
                  Back
                </Link>
              </Button>
              <Button onClick={() => start.mutate(channel)} disabled={start.isPending}>
                Continue with {PROVIDER_LABELS[channel]}
                <ArrowRight />
              </Button>
            </div>
            {sandbox && <p className="ws-hint">Sandbox mode: authorisation is simulated and no Meta account is contacted.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
